import Stripe from "stripe";
import { z } from "zod/v4";

import { OrgShopIdDtoSchema } from "../dtos/organization-shop-id";
import {
  StripeSubscriptionStatus,
  TRIAL_PRICE_FOR_METADATA_KEY,
} from "../dtos/organization-subscriptions";
import { StripeEntitlementFeatureMetadata } from "../dtos/stripe-api/price";
import { UpcomingPriceChangeDto } from "../dtos/stripe-subscription-links";
import {
  StripeInterval,
  SubscriptionBillingProviderList,
} from "../dtos/subscriptions";
import { castToBool, castToBoolLenient, castToNumber } from "../utils";

export const StripeSubscriptionIntervalList = [
  "day",
  "month",
  "week",
  "year",
] as const;

export type StripeSubscriptionInterval = Stripe.Price.Recurring.Interval;

export const CouponSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  amountOff: z.number().nullish(),
  percentOff: z.number().nullish(),
});

export type CouponType = z.infer<typeof CouponSchema>;

export type StripeMeterEventPayload = {
  value: number;
  customerId: string;
};

export type StripeMonth =
  `${number}-${"01" | "02" | "03" | "04" | "05" | "06" | "07" | "08" | "09" | "10" | "11" | "12"}`;

export const STRIPE_LATEST_PRODUCT_VERSION = "v3";

export const BillingCatalogVersionList = [
  "v1",
  "v2",
  "v3",
  "2026_module_v1",
] as const;

export type BillingCatalogVersion = (typeof BillingCatalogVersionList)[number];

// Backwards-compatible alias used throughout Stripe billing flows.
export const StripeProductVersionList = BillingCatalogVersionList;
export type StripeProductVersion = BillingCatalogVersion;

export const StripeNewFormatVersionList: readonly StripeProductVersion[] = [
  "v2",
  "v3",
  "2026_module_v1",
] as const;

export interface StripeEntitlementFeature extends Stripe.Entitlements.Feature {
  metadata: StripeEntitlementFeatureMetadata;
}

export interface StripeProductFeature extends Stripe.ProductFeature {
  entitlement_feature: StripeEntitlementFeature;
}

export const StripeInvoiceMetadataSchema = z.object({
  billingProvider: z.enum(SubscriptionBillingProviderList),
  billingApproved: castToBool,
  billingEverApproved: castToBool.nullish(),
  mistakenlyMarkedPaid: castToBool.nullish(),
});

export type StripeInvoiceMetadata = z.infer<typeof StripeInvoiceMetadataSchema>;

export type StripeInvoice = Stripe.Invoice & {
  id: string;
};

export const RedeemedOfferSchema = z.object({
  redeemedAt: z.string(),
  redeemedBy: z.string().optional(),
  couponId: z.string().optional(),
});

// A redemption to record under `offerKey`. `redeemedAt` is fixed by whoever
// plans the write, so a retried write sends the same params under its
// idempotency key.
export const OfferRedemptionSchema = RedeemedOfferSchema.extend({
  offerKey: z.string().min(1),
});

export type OfferRedemption = z.infer<typeof OfferRedemptionSchema>;

export const StripeSubscriptionMetadataSchema = z.object({
  billingProvider: z.enum(SubscriptionBillingProviderList).catch((ctx) => {
    console.warn("Malformed billingProvider metadata, defaulting to 'stripe'", {
      value: ctx.value,
    });
    return "stripe" as const;
  }),
  billingApproved: castToBool.catch((ctx) => {
    console.warn("Malformed billingApproved metadata, defaulting to false", {
      value: ctx.value,
    });
    return false;
  }),
  billingEverApproved: castToBool.nullish(),
  // Labels a still-pending, reversible cancellation that the Shopify lifecycle
  // lane scheduled (a freeze, an uninstall). A merchant's own scheduled cancel
  // leaves it absent, which is what lets a recovery path release only the
  // cancellation that lane scheduled. Cleared the moment the cancellation is
  // released or superseded ("" deletes the key in Stripe), so its presence on a
  // non-terminal subscription means that cancellation is still armed.
  pendingCancelReason: z.string().nullish(),
  orgs: OrgShopIdDtoSchema.array().catch((ctx) => {
    console.warn("Malformed orgs metadata, defaulting to []", {
      value: ctx.value,
    });
    return [];
  }),
  autoScale: castToBool.optional(),
  version: z.enum(StripeProductVersionList).catch((ctx) => {
    console.warn("Malformed version metadata, defaulting to 'v1'", {
      value: ctx.value,
    });
    return "v1" as const;
  }),
  hideMissingPaymentAlert: z.string().nullish(),
  shopifyAppSubscriptionId: z.string().optional(),
  agencyEmails: z
    .string()
    .array()
    .nullish()
    .catch((ctx) => {
      console.warn("Malformed agencyEmails metadata, defaulting to []", {
        value: ctx.value,
      });
      return [];
    }),
  disableShopifyChargesUntil: z.string().nullish(),
  // A command-owned Shopify charge hold: while non-empty, this bridge's invoices
  // may not be posted to Shopify because its provider state is provisional. Only
  // the Command whose bridge mutation receipt carries the matching token may
  // clear it ("" deletes the key in Stripe).
  shopifyChargeHold: z.string().nullish(),
  shopId: z.string().nullish(),
  redeemedOffers: z.record(z.string(), RedeemedOfferSchema).nullish(),
  brandManagementBeta: castToBool.optional(),
});

export type StripeSubscriptionMetadata = z.infer<
  typeof StripeSubscriptionMetadataSchema
>;

/**
 * The subscription's `redeemedOffers` with this redemption added, or undefined
 * when the offer is already recorded: the first redemption is the one that
 * counts, and rewriting it would move its date.
 */
export function recordOfferRedemption(
  redeemedOffers: StripeSubscriptionMetadata["redeemedOffers"],
  redemption: OfferRedemption
): NonNullable<StripeSubscriptionMetadata["redeemedOffers"]> | undefined {
  if (redeemedOffers?.[redemption.offerKey] !== undefined) {
    return undefined;
  }
  const { offerKey, ...entry } = redemption;
  return { ...redeemedOffers, [offerKey]: entry };
}

export const StripeProductMetadataSchema = z.object({
  level: castToNumber.nullish().default(-1),
  version: z.enum(StripeProductVersionList).catch((ctx) => {
    console.warn("Malformed product version metadata, defaulting to 'v1'", {
      value: ctx.value,
    });
    return "v1" as const;
  }),
  features: z.string().array(),
  freeTrialDays: castToNumber.nullish(),
  maxOrderCount: castToNumber.nullish(),
  entitlement_profile_key: z.string().nullish(),
  ui_hidden: castToBoolLenient,
});

export type StripeProductMetadata = z.infer<typeof StripeProductMetadataSchema>;

export const StripeApiPriceTemplateDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  tierMap: z.record(z.string(), z.string().array()),
  liveTemplate: castToBool,
});

export type StripeApiPriceTemplateDto = z.infer<
  typeof StripeApiPriceTemplateDtoSchema
>;

export const StripePriceMetadataSchema = z.object({
  comparePrice: castToNumber.nullish(),
  templates: StripeApiPriceTemplateDtoSchema.array(),
  tier: z.string().nullish(),
  version: z.string().nullish(),
  active: castToBool.nullish(),
  freeTrialDays: castToNumber.nullish(),
  isLive: castToBool.nullish(),
  // Set only on a Trial Price: the Module Price it converts to at trial end.
  [TRIAL_PRICE_FOR_METADATA_KEY]: z.string().nullish(),
});

export type StripePriceMetadata = z.infer<typeof StripePriceMetadataSchema>;

export const StripeSubscriptionItemMetadataSchema = z.object({
  defaultQuantity: castToNumber.nullish(),
});

export type StripeSubscriptionItemMetadata = z.infer<
  typeof StripeSubscriptionItemMetadataSchema
>;

// Per-item trial state from the Stripe Trial Offers preview API
// (`Stripe-Version: 2026-03-25.preview`). Mirrors the `items[].current_trial`
// response shape: epoch-second `start_date`/`end_date` and the attached
// reusable Trial Offer id. Hand-written because Trial Offers is preview-only
// and absent from the stable Stripe SDK types. Absent when an item is not
// trialing.
export const StripeSubscriptionItemCurrentTrialSchema = z.object({
  start_date: z.number(),
  end_date: z.number(),
  trial_offer: z.string(),
});

export type StripeSubscriptionItemCurrentTrial = z.infer<
  typeof StripeSubscriptionItemCurrentTrialSchema
>;

export type StripeSubscriptionItem = Omit<
  Stripe.SubscriptionItem,
  "metadata" | "price"
> & {
  price: StripePrice;
  metadata: StripeSubscriptionItemMetadata;
  current_trial?: StripeSubscriptionItemCurrentTrial | null;
};

// Stripe billing engine mode. Modular subs start classic and are migrated to
// flexible on the first module-trial grant (ADR 0013); Trial Offers (and their
// item-level current_trial) only exist on flexible subs.
export const StripeBillingModeSchema = z.enum(["classic", "flexible"]);
export type StripeBillingMode = z.infer<typeof StripeBillingModeSchema>;

export type StripeSubscriptionSchedulePhaseItem = Omit<
  Stripe.SubscriptionSchedule.Phase.Item,
  "metadata"
> & {
  metadata: StripeSubscriptionItemMetadata;
};

export type StripeSubscriptionSchedulePhase = Omit<
  Stripe.SubscriptionSchedule.Phase,
  "items"
> & {
  items: StripeSubscriptionSchedulePhaseItem[];
};

export type StripeSubscriptionSchedule = Omit<
  Stripe.SubscriptionSchedule,
  "phases"
> & {
  phases: StripeSubscriptionSchedulePhase[];
  current_phase: {
    start_date: number;
    end_date: number;
  } | null;
};

export type CachedStripePrice = {
  active: boolean;
  stripePriceId: string;
  stripeProductId: string;
  nickname: string | null;
  productName: string;
  unitAmount: number | null;
  metadata: StripePriceMetadata;
  tiers: Stripe.Price.Tier[] | undefined;
  entitlementProfileKey?: string | null;
  productFreeTrialDays?: StripeProductMetadata["freeTrialDays"] | null;
  productMaxOrderCount?: number | null;
  productLevel?: number | null;
  productVersion?: StripeProductVersion;
  productFeatures?: string[];
  productUiHidden?: boolean | null;
  interval?: StripeInterval;
  // Optional only because rows cached before this field existed carry no value.
  // SyncStripePriceCacheCron rewrites every row every 20 minutes, so the gap closes on its own.
  intervalCount?: number;
};

// A Stripe customer is stamped once at creation with exactly one owner: orgId
// for an org-billed customer, agencyId for an agency-billed one. Both are
// optional here because legacy customers predate either stamp.
export const StripeCustomerMetadataSchema = z.object({
  orgId: z.string().optional(),
  agencyId: z.string().optional(),
});

export type StripeCustomerMetadata = z.infer<
  typeof StripeCustomerMetadataSchema
>;

export type CachedStripeCustomer = {
  id: string;
  name: string;
  email?: string | null;
  billing: boolean;
  hasAddressInfo: boolean;
  hasActiveStripeSubscription: boolean;
  hasActiveShopifySubscription: boolean;
  metadata?: StripeCustomerMetadata;
  // Epoch ms when this projection was last built from a live Stripe customer.
  // Absent on items written before the stamp existed; treat absent as stale.
  lastSyncedAt?: number;
};

// One entry of a subscription's Discount Stack, projected from a live Stripe
// discount. `source` is derived from the coupon id via isModularBundleCouponId:
// the system-owned Bundle Discount vs an operator-added Operator Coupon. `end`
// carries a repeating coupon's expiry. See ADR-0009.
export type CachedSubscriptionDiscount = {
  discountId: string;
  couponId?: string;
  percentOff?: number;
  amountOff?: number;
  name?: string;
  end?: number;
  source: "bundle" | "operator";
};

export type CachedStripeSubscription = {
  partitionKey: string;
  stripeSubscriptionId: string;
  orgId: string | null;
  startDate?: number;
  periodStart: number;
  periodEnd: number;
  // Stripe's `billing_cycle_anchor`. Its day of month is the one every period
  // end returns to, clamped in a shorter month; `periodEnd` alone cannot say
  // whether its day was clamped. Absent on projections written before the
  // cache carried it.
  billingCycleAnchor?: number;
  items: StripePhaseOrLineItemItems;
  liveItems?: StripeSubscriptionItem[];
  // Absent on projections written before this field existed; treat absent as
  // classic. The Trial Offers preview retrieve is gated on flexible (ADR 0013).
  billingMode?: StripeBillingMode;
  cancelAt: number | null;
  pauseAt: number | null;
  trialEnd: number | null;
  // Stripe's `ended_at`: when the subscription actually stopped, as opposed to
  // `cancelAt`, which is a *scheduled* future cancellation and is nulled once it
  // passes.
  endedAt?: number | null;
  stripeSubscriptionScheduleId: string | null;
  upcomingPrices: UpcomingPriceChangeDto[];
  endingPrices: UpcomingPriceChangeDto[];
  agencyEmails?: string[] | null;
  metadata: StripeSubscriptionMetadata;
  interval: StripeInterval;
  intervalCount: number;
  isAgencySub: boolean;
  status: StripeSubscriptionStatus;
  stripePriceId: string;
  version: string | null | undefined;
  // Deprecated single-coupon alias (largest discount via pickBestCoupon). Retained
  // for current readers; new code reads the full ordered `discounts` stack.
  coupon?: {
    id?: string;
    percentOff?: number;
    amountOff?: number;
  };
  // Full ordered Discount Stack projected from the live subscription.discounts.
  discounts?: CachedSubscriptionDiscount[];
  customer: {
    id: string;
    billing: boolean;
  };
  createdAtTs?: number;
};

export type StripeSubscription = Omit<
  Stripe.Subscription,
  "metadata" | "items" | "schedule"
> & {
  metadata: StripeSubscriptionMetadata;
  items: Stripe.ApiList<StripeSubscriptionItem>;
  schedule: StripeSubscriptionSchedule | null;
};

export type StripeProduct = Omit<Stripe.Product, "metadata"> & {
  metadata: StripeProductMetadata;
};

export type StripePrice = Omit<Stripe.Price, "metadata" | "product"> & {
  metadata: StripePriceMetadata;
  product: string | StripeProduct;
};

export interface StripeCustomerSubscriptionCreatedEvent {
  object: StripeSubscription;
  previous_attributes?: Partial<StripeSubscription>;
}

export type StripePhaseOrLineItemItem =
  | StripeSubscriptionItem
  | StripeSubscriptionSchedulePhaseItem;

export type StripePhaseOrLineItemItems =
  | StripeSubscriptionItem[]
  | StripeSubscriptionSchedulePhaseItem[];
